<template>
  <v-autocomplete
    outlined
    dense
    :append-icon="additionalClaimantOptions && additionalClaimantOptions.length ? undefined : ''"
    :color="hasError ? 'error' : 'brandCyan'"
    class="rounded form-dropdown"
    :class="{ 'invalid-input': hasError }"
    :label="label"
    :items="additionalClaimantOptions"
    item-text="displayName"
    item-value="userId"
    return-object
    cache-items
    multiple
    small-chips
    deletable-chips
    hide-details
    :loading="additionalClaimantLoading"
    :search-input.sync="additionalClaimantSearch"
    no-data-text="Type at least 3 letters to search"
    v-model="internalValue"
    @change="onSelectionChange"
  >
    <!-- Append-item slot for infinite scrolling -->
    <template v-slot:append-item>
      <v-list-item v-if="additionalClaimantHasMore" v-intersect="onAdditionalClaimantIntersect">
        <v-list-item-title>Loading more...</v-list-item-title>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
import UserService from "@/services/UserService";
import { mapState } from "vuex";
import { debounce } from "@/shared_data/functions";
export default {
  name: "AdditionalClaimantsInput",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    additionalClaimantRule: {
      type: Object,
      default: null,
    },
    additionalClaimantUserAttributes: {
      type: Object,
      default: () => ({ min: undefined, max: undefined }),
    },
    label: {
      type: String,
      default: "",
    },
    hasError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      additionalClaimantSearch: "",
      additionalClaimantOptions: [],
      additionalClaimantLoading: false,
      additionalClaimantDebouncer: null,
      additionalClaimantRequestSeq: 0,
      additionalClaimantPageSize: 20,
      additionalClaimantOffset: 0,
      additionalClaimantHasMore: false,
      additionalClaimantSearchResults: [],
    };
  },
  computed: {
    ...mapState(["userProfile", "globalClientId"]),
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    HasAdditionalClaimantConstraints() {
      return !!this.additionalClaimantRule;
    },
    currentClientId() {
      return this.additionalClaimantRule?.clientId;
    },
  },
  created() {
    this.additionalClaimantDebouncer = debounce(() => {
      this.fetchAdditionalClaimantUsers(true);
    }, 300);
  },
  mounted() {
    this.rebuildAdditionalClaimantOptions();
  },
  watch: {
    additionalClaimantSearch: function (n) {
      if (!this.HasAdditionalClaimantConstraints) return;
      const search = (n || "").trim();
      if (search.length < 3) {
        this.additionalClaimantOptions = [];
        this.additionalClaimantSearchResults = [];
        this.additionalClaimantOffset = 0;
        this.additionalClaimantHasMore = false;
        this.additionalClaimantLoading = false;
        // Invalidate any pending requests
        this.additionalClaimantRequestSeq += 1;
        return;
      }
      this.scheduleAdditionalClaimantFetch();
    },
    value: {
      handler() {
        this.rebuildAdditionalClaimantOptions();
      },
      deep: true,
    },
  },
  methods: {
    onSelectionChange(val) {
      const selectedRaw = Array.isArray(val) ? val : val ? [val] : this.value || [];
      const selected = selectedRaw.filter(Boolean);
      const idList = selected
        .map((u) => (u && typeof u === "object" ? u.userId : u))
        .filter((id) => id !== undefined && id !== null && id !== "")
        .join(",");
      this.$emit("selection-change", { selectedUsers: selected, idList });
    },
    scheduleAdditionalClaimantFetch() {
      if (!this.additionalClaimantDebouncer) {
        this.additionalClaimantDebouncer = debounce(() => {
          this.fetchAdditionalClaimantUsers(true);
        }, 300);
      }
      this.additionalClaimantDebouncer();
    },
    rebuildAdditionalClaimantOptions() {
      const selectedUsers = (this.value || []).filter(Boolean);

      // Keep options unique by userId
      const uniq = new Map();
      (this.additionalClaimantSearchResults || []).forEach((u) => {
        if (u && u.userId) uniq.set(u.userId, u);
      });
      if (selectedUsers.length) {
        selectedUsers.forEach((u) => {
          if (!u || !u.userId) return;
          const displayName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
          uniq.set(u.userId, { ...u, displayName });
        });
      }
      this.additionalClaimantOptions = Array.from(uniq.values());
    },
    escapeFilterString(val) {
      // Basic escaping for single quotes in the filter DSL
      return (val || "").replace(/'/g, "\\'");
    },
    buildAdditionalClaimantFilter(search) {
      const attrs = this.additionalClaimantUserAttributes || {};
      const normalizeToArray = (value) => {
        if (value === undefined || value === null) return [];
        if (Array.isArray(value)) return value.filter(Boolean);
        if (typeof value === "string" && value.trim()) return [value.trim()];
        return [];
      };

      let filter = "status != 'Deleted'";
      if (this.currentClientId !== undefined && this.currentClientId !== null) {
        filter += ` && clientId == ${this.currentClientId}`;
      }
      // Constrain by userAttributes
      const attributeClauses = [];
      const attributeKeys = Object.keys(attrs || {}).filter(
        (key) => key !== "min" && key !== "max"
      );
      attributeKeys.forEach((key) => {
        const values = normalizeToArray(attrs[key]);
        if (values.length) attributeClauses.push(`${key}.isIn(${JSON.stringify(values)})`);
      });
      if (attributeClauses.length) {
        filter += ` && (${attributeClauses.join(" && ")})`;
      }

      const q = (search || "").trim();
      if (q) {
        const escaped = this.escapeFilterString(q);
        filter += ` && (firstName.like('%${escaped}%') || lastName.like('%${escaped}%'))`;
      }
      return filter;
    },
    async fetchAdditionalClaimantUsers(reset = true) {
      if (!this.HasAdditionalClaimantConstraints) return;
      const search = (this.additionalClaimantSearch || "").trim();
      if (search.length < 3) {
        this.additionalClaimantOptions = [];
        this.additionalClaimantSearchResults = [];
        this.additionalClaimantOffset = 0;
        this.additionalClaimantHasMore = false;
        this.additionalClaimantLoading = false;
        return;
      }

      if (!reset) {
        if (this.additionalClaimantLoading) return;
        if (!this.additionalClaimantHasMore) return;
      } else {
        // Reset pagination and current results for a new search
        this.additionalClaimantOffset = 0;
        this.additionalClaimantHasMore = false;
        this.additionalClaimantSearchResults = [];
        this.additionalClaimantOptions = [];
      }

      const requestId = ++this.additionalClaimantRequestSeq;
      this.additionalClaimantLoading = true;
      try {
        const filter = this.buildAdditionalClaimantFilter(search);
        const response = await UserService.getUsersV2({
          filter,
          limit: this.additionalClaimantPageSize,
          offset: this.additionalClaimantOffset,
          extract: "userId,firstName,lastName",
        });

        if (requestId !== this.additionalClaimantRequestSeq) return;
        if ((this.additionalClaimantSearch || "").trim() !== search) return;

        const rows = (response && response.result && response.result.rows) || [];
        const cachedRows = rows.map((u) => ({
          ...u,
          displayName: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
        }));

        // Append unique results for infinite scrolling
        const uniq = new Map(
          (this.additionalClaimantSearchResults || [])
            .filter((u) => u && u.userId)
            .map((u) => [u.userId, u])
        );
        cachedRows.forEach((u) => {
          if (u && u.userId) uniq.set(u.userId, u);
        });
        this.additionalClaimantSearchResults = Array.from(uniq.values());

        // Update pagination state
        const totalCount =
          response && response.result && Number.isFinite(response.result.count)
            ? Number(response.result.count)
            : null;
        this.additionalClaimantOffset += cachedRows.length;
        this.additionalClaimantHasMore =
          typeof totalCount === "number"
            ? this.additionalClaimantOffset < totalCount
            : cachedRows.length === this.additionalClaimantPageSize;

        // Combine search results with selected users to preserve them in options
        this.rebuildAdditionalClaimantOptions();
      } catch (e) {
        if (requestId !== this.additionalClaimantRequestSeq) return;
        this.additionalClaimantOptions = [];
        this.additionalClaimantSearchResults = [];
        this.additionalClaimantOffset = 0;
        this.additionalClaimantHasMore = false;
      } finally {
        if (requestId === this.additionalClaimantRequestSeq) {
          this.additionalClaimantLoading = false;
        }
      }
    },
    onAdditionalClaimantIntersect(entries, observer, isIntersecting) {
      // Vuetify v-intersect passes (entries, observer, isIntersecting)
      if (!isIntersecting) return;
      this.fetchAdditionalClaimantUsers(false);
    },
  },
};
</script>
<style>
.form-dropdown.v-select--chips.v-text-field--outlined.v-input--dense.v-input--is-label-active
  .v-select__selections {
  margin-top: 10px;
}
</style>
